import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { ActivityReservation } from "@b2bportal/rbc-activities-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./BookingDetailsSection.module.scss";
import { useBookingDetailsSection } from "./useBookingDetailsSection";

interface BookingDetailsSectionProps {
  reservation: ActivityReservation;
  className?: string;
}

export const BookingDetailsSection = ({
  reservation,
  className,
}: BookingDetailsSectionProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "BookingDetailsSection");

  const {
    confirmationCode,
    bookerInfo,
    bookerPhone,
    bookerEmail,
    supplierName,
    supplierPhone,
    supplierEmail,
    hasTravellerContact,
    hasTourOperator,
  } = useBookingDetailsSection(reservation);

  if (!reservation.bookingDetails) return null;

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>
        {t("activities.postBooking.bookingDetails.title")}
      </h3>

      <div className={cn("section")}>
        <div className={cn("field")}>
          <div className={cn("label")}>
            {t("tripSearch.confirmationCode")}
            <Icon iconName={IconNameEnum.info} className={cn("label-icon")} />
          </div>
          <p className={cn("value")}>{confirmationCode}</p>
        </div>
      </div>

      {hasTourOperator && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <div className={cn("label")}>
              {t("activities.postBooking.bookingDetails.tourOperator")}
            </div>
            {supplierName && <p className={cn("value")}>{supplierName}</p>}
          </div>
          <div className={cn("field")}>
            <div className={cn("label")}>{t("contactInformation")}</div>
            {supplierPhone && <p className={cn("value")}>{supplierPhone}</p>}
            {supplierEmail && (
              <a
                className={cn("value", ["link"])}
                href={`mailto:${supplierEmail}`}
                target="_blank"
                rel="noreferrer"
              >
                {supplierEmail}
              </a>
            )}
          </div>
        </div>
      )}

      {bookerInfo && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <div className={cn("label")}>
              {t("activities.postBooking.bookingDetails.primaryTraveler")}
            </div>
            <p className={cn("value")}>
              {bookerInfo.firstName} {bookerInfo.lastName}
            </p>
          </div>
        </div>
      )}

      {hasTravellerContact && (
        <div className={cn("section")}>
          <div className={cn("field")}>
            <div className={cn("label")}>{t("contactInformation")}</div>
            {bookerPhone && <span className={cn("value")}>{bookerPhone}</span>}
            {bookerEmail && <p className={cn("value")}>{bookerEmail}</p>}
          </div>
        </div>
      )}
    </div>
  );
};
